import { useCallback, useRef, useState } from 'react';
import { apiService } from '../../../services/api';
import { eventBus, EventNames } from '../../../utils/eventBus';
import type { ChatMessage } from '../../ChatPanel';
import logger from '../../../utils/simpleLogger';

/**
 * Hook para gestionar acciones sobre mensajes
 *
 * Responsabilidades:
 * - Eliminar mensajes (con manejo especial de Studio)
 * - Copiar contenido de mensajes
 * - Guardar/desguardar mensajes
 * - Editar mensajes (trigger para re-envío)
 */

export interface UseMessageActionsOptions {
  messages: ChatMessage[];
  onMessagesUpdate: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  onMessageSave?: (
    message: ChatMessage,
    title: string,
    notes?: string,
    tags?: string[]
  ) => Promise<void>;
}

export interface MessageActionsState {
  successMessage: string | null;
  error: string | null;
}

export function useMessageActions(options: UseMessageActionsOptions) {
  const { messages, onMessagesUpdate, onMessageSave } = options;

  const [state, setState] = useState<MessageActionsState>({
    successMessage: null,
    error: null,
  });
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // D07: Guard against rapid double-clicks on save/unsave
  const isSavingRef = useRef(false);

  const setSuccessMessage = useCallback((message: string | null) => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    setState((prev) => ({ ...prev, successMessage: message }));
    if (message) {
      successTimeoutRef.current = setTimeout(
        () => setState((prev) => ({ ...prev, successMessage: null })),
        2000
      );
    }
  }, []);

  const setError = useCallback((error: string | null) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setState((prev) => ({ ...prev, error }));
    if (error) {
      errorTimeoutRef.current = setTimeout(
        () => setState((prev) => ({ ...prev, error: null })),
        3000
      );
    }
  }, []);

  /**
   * Delete a message
   */
  const deleteMessage = useCallback(
    async (messageId: string) => {
      const targetMessage = messages.find((m) => m.id === messageId);

      // Handle Studio results specially
      if (targetMessage?.studioResult || targetMessage?.studioPrompt) {
        const prefix = targetMessage.id.startsWith('studio-')
          ? targetMessage.id.split('-').slice(0, 3).join('-')
          : targetMessage.id;
        onMessagesUpdate((prev) => prev.filter((m) => !m.id.startsWith(prefix)));
        setSuccessMessage('Resultado del Studio eliminado del chat');
        return;
      }

      // Confirmación removida (window.confirm bloquea MCP automation)

      try {
        const response = await apiService.deleteMessage(messageId);

        if (response.success) {
          onMessagesUpdate((prev) => prev.filter((m) => m.id !== messageId));
          setSuccessMessage('Mensaje eliminado');
        }
      } catch (err: unknown) {
        console.error('Error deleting message:', err);
        setError('Error al eliminar el mensaje');
      }
    },
    [messages, onMessagesUpdate, setSuccessMessage, setError]
  );

  /**
   * Copy message content to clipboard
   */
  const copyMessage = useCallback(
    (content: string) => {
      navigator.clipboard.writeText(content);
      setSuccessMessage('Mensaje copiado');
    },
    [setSuccessMessage]
  );

  /**
   * Generate automatic title from content
   */
  const generateAutoTitle = useCallback((content: string): string => {
    if (!content) return 'Mensaje sin título';

    const cleanContent = content
      .replace(/^#+\s+/gm, '')
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/\[(\d+)\]/g, '')
      .trim();

    let title = cleanContent.substring(0, 60);
    const firstPeriod = title.indexOf('.');
    const firstNewline = title.indexOf('\n');

    if (firstPeriod > 0 && firstPeriod < 60) {
      title = title.substring(0, firstPeriod);
    } else if (firstNewline > 0 && firstNewline < 60) {
      title = title.substring(0, firstNewline);
    } else if (title.length === 60) {
      const lastSpace = title.lastIndexOf(' ');
      if (lastSpace > 40) {
        title = title.substring(0, lastSpace);
      }
      title += '...';
    }

    return title.trim() || 'Mensaje guardado';
  }, []);

  /**
   * Save or unsave a message (D07: toggle with guard against double-clicks)
   */
  const saveMessage = useCallback(
    async (messageId: string) => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;

      try {
        const message = messages.find((m) => m.id === messageId);
        if (!message) {
          setError('Mensaje no encontrado');
          return;
        }

        // If already saved, unsave it
        if (message.is_saved) {
          try {
            const savedMessages = await apiService.getSavedMessages();
            const savedMessage = savedMessages.find(
              (sm: { message_id: string }) => sm.message_id === messageId
            );

            if (savedMessage) {
              await apiService.deleteSavedMessage(savedMessage.id);

              onMessagesUpdate((prev) =>
                prev.map((m) => (m.id === messageId ? { ...m, is_saved: false } : m))
              );

              eventBus.emit(EventNames.SAVED_ITEM_DELETED, {
                type: 'message',
                messageId,
                projectId: undefined,
              });

              setSuccessMessage('Mensaje quitado de guardados');
            }
          } catch (err: unknown) {
            const error = err as Error;
            console.error('❌ Error desguardando mensaje:', err);
            setError('Error al quitar de guardados: ' + (error.message || 'Error desconocido'));
          }
          return;
        }

        // Save the message
        try {
          const autoTitle = generateAutoTitle(message.markdown ?? message.text);

          if (onMessageSave) {
            await onMessageSave(message, autoTitle, undefined, []);
          } else {
            await apiService.saveChatMessage({
              message_id: messageId,
              title: autoTitle,
              notes: undefined,
              tags: [],
              content: message.markdown ?? message.text,
            });

            eventBus.emit(EventNames.SAVED_ITEM_CREATED, {
              type: 'message',
              messageId,
              projectId: undefined,
            });
          }

          onMessagesUpdate((prev) =>
            prev.map((m) => (m.id === messageId ? { ...m, is_saved: true } : m))
          );

          setSuccessMessage('Mensaje guardado');
        } catch (err: unknown) {
          const error = err as Error;
          console.error('❌ Error guardando mensaje:', err);
          if (error.message?.includes('already saved')) {
            setError('Este mensaje ya está guardado');
          } else {
            setError('Error al guardar el mensaje: ' + (error.message || 'Error desconocido'));
          }
        }
      } finally {
        isSavingRef.current = false;
      }
    },
    [messages, onMessagesUpdate, onMessageSave, generateAutoTitle, setSuccessMessage, setError]
  );

  /**
   * Edit a message (loads it back into input for re-sending)
   */
  const editMessage = useCallback(
    async (messageId: string) => {
      const message = messages.find((m) => m.id === messageId);
      if (!message) return;

      try {
        const response = await apiService.editMessage(messageId);

        if (response.success) {
          onMessagesUpdate((prev) => {
            const messageIndex = prev.findIndex((msg) => msg.id === messageId);
            if (messageIndex === -1) return prev;
            return prev.filter((_, index) => {
              if (index === messageIndex) return false;
              if (index === messageIndex + 1 && !prev[index].isUser) return false;
              return true;
            });
          });

          eventBus.emit(EventNames.MESSAGE_EDIT_REQUESTED, {
            originalContent: response.original_content,
          });
        }
      } catch (err: unknown) {
        console.error('Error editing message:', err);
      }
    },
    [messages, onMessagesUpdate]
  );

  /**
   * Rate a message (thumbs up = 5, thumbs down = 1)
   * Optimistic update with rollback on failure.
   */
  const ratingInFlight = useRef<string | null>(null);
  const rateMessage = useCallback(
    async (messageId: string, rating: 1 | 5, feedback?: string) => {
      // Guard: prevent duplicate calls while one is in-flight
      if (ratingInFlight.current === messageId) return;
      ratingInFlight.current = messageId;

      const message = messages.find((m) => m.id === messageId);
      if (!message) {
        ratingInFlight.current = null;
        return;
      }

      // Capture previous state for rollback BEFORE any update
      const previousRating = message.user_rating ?? null;
      // Toggle: if same rating clicked again, remove it
      const newRating = message.user_rating === rating ? null : rating;

      // Optimistic update — show change immediately
      onMessagesUpdate((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, user_rating: newRating } : m))
      );

      try {
        // Persist to backend
        const apiRating = newRating ?? 0;
        await apiService.rateMessage(messageId, apiRating, feedback);
      } catch (err: unknown) {
        logger.error('Error rating message:', err);
        // Rollback: restore previous rating
        onMessagesUpdate((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, user_rating: previousRating } : m))
        );
      } finally {
        ratingInFlight.current = null;
      }
    },
    [messages, onMessagesUpdate]
  );

  /**
   * Regenerate an AI response (delete pair, re-send user message)
   */
  const regenerateMessage = useCallback(
    async (aiMessageId: string) => {
      const aiIndex = messages.findIndex((m) => m.id === aiMessageId);
      if (aiIndex <= 0) return;

      const userMessage = messages[aiIndex - 1];
      if (!userMessage?.isUser) return;

      const originalText = userMessage.text;

      try {
        // Use edit API to delete user+AI pair from DB
        const response = await apiService.editMessage(userMessage.id);

        if (response.success) {
          // Remove both from UI
          onMessagesUpdate((prev) => {
            const msgIndex = prev.findIndex((m) => m.id === userMessage.id);
            if (msgIndex === -1) return prev;
            return prev.filter((_, index) => {
              if (index === msgIndex) return false;
              if (index === msgIndex + 1 && !prev[index].isUser) return false;
              return true;
            });
          });

          // Re-send immediately via existing event
          eventBus.emit(EventNames.SUGGESTED_QUESTION_CLICKED, {
            question: originalText,
            autoSend: true,
          });
        }
      } catch (err: unknown) {
        logger.error('Error regenerating message:', err);
        setError('Error al regenerar la respuesta');
      }
    },
    [messages, onMessagesUpdate, setError]
  );

  return {
    // State
    ...state,

    // Actions
    deleteMessage,
    copyMessage,
    saveMessage,
    editMessage,
    rateMessage,
    regenerateMessage,

    // State setters (for external control)
    setSuccessMessage,
    setError,
  };
}
